import { useCallback, useEffect, useRef } from "react";
import { useSearchParams, type SetURLSearchParams } from "react-router-dom";

interface UseSearchQueryStateOptions {
  namespace?: string;
}

/**
 * Syncs a text search input with a `?q=` URL query param (or `{namespace}_q`
 * for screens with more than one search box), the same way usePageState
 * syncs page/pageSize — so search terms are bookmarkable and survive reload.
 * See usePageState.ts for the stability rationale behind the ref pattern.
 */
export function useSearchQueryState(options?: UseSearchQueryStateOptions): [string, (value: string) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const setSearchParamsRef = useRef<SetURLSearchParams>(setSearchParams);
  useEffect(() => {
    setSearchParamsRef.current = setSearchParams;
  });

  const key = options?.namespace ? `${options.namespace}_q` : "q";
  const value = searchParams.get(key) ?? "";

  const setValue = useCallback(
    (next: string) => {
      setSearchParamsRef.current(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next) {
            params.set(key, next);
          } else {
            params.delete(key);
          }
          return params;
        },
        { replace: true },
      );
    },
    [key],
  );

  return [value, setValue];
}
